import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  CircularProgress,
} from "@mui/material";
import React, { useRef } from "react";

export type FilterSelectOption = {
  label: string;
  value: string;
};

type BaseProps = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  options: FilterSelectOption[];
  placeholder?: string;
};

type MuiAutocompleteExtras = Omit<
  AutocompleteProps<FilterSelectOption, false, false, false>,
  | "options"
  | "value"
  | "onChange"
  | "inputValue"
  | "onInputChange"
  | "renderInput"
  | "getOptionLabel"
  | "isOptionEqualToValue"
>;

type FilterSelectProps = BaseProps & Partial<MuiAutocompleteExtras>;

const FilterSelect: React.FC<FilterSelectProps> = ({
  label = "Filter",
  value,
  onChange,
  inputValue,
  onInputChange,
  options,
  loading = false,
  placeholder,
  ...autocompleteExtras
}) => {
  const lastKnownOption = useRef<FilterSelectOption | null>(null);

  const matchedOption = options.find((opt) => opt.value === value);
  if (matchedOption) {
    lastKnownOption.current = matchedOption;
  }

  const selectedOption =
    lastKnownOption.current?.value === value
      ? lastKnownOption.current
      : value
        ? { label: lastKnownOption.current?.label ?? value, value }
        : null;

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onChange={(_, newVal) => onChange(newVal?.value || "")}
      onInputChange={(_, newInput, reason) => {
        if (reason !== "reset") onInputChange(newInput);
      }}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.value === b?.value}
      loading={loading}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...autocompleteExtras}
    />
  );
};

export default FilterSelect;
